// useDashboardData.ts
import { useEffect, useState } from "react";
import { DashboardData } from "../types/dashboard";
import api from "../../../../../features/auth/axiosConfig";
import { useUser } from "src/shared/context/UserContext";
import { errorHandling } from "src/shared/services/errorHandling";

type Mode = "year" | "range";

interface UseDashboardDataProps {
  mode: Mode;
  year?: number;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

export const useDashboardData = ({ mode, year, startDate, endDate, enabled = true }: UseDashboardDataProps) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const { user } = useUser();
  const loginUser = user?.role[0] === "User";

  const fetchData = async () => {
    try {
      let params: any;
      if (mode === "year") {
        if (typeof year !== "number") return;
        params = { year };
      }
      if (mode === "range") {
        if (!startDate || !endDate) return;
        if (new Date(startDate) > new Date(endDate)) return;
        params = { start_date: startDate, end_date: endDate };
      }

      if (userId !== "undefined" && !loginUser) {
        params.user_id = userId ?? "";
      }

      if (loginUser) {
        params.user_id = user.userId[0];
      }

      setLoading(true);
      setError(null);
      const res = await api.get("/app/pos/v2/dashboard", { params });
      setDashboardData(res.data);
    } catch (err: any) {
      setDashboardData(null);
      errorHandling(err);
      setError(err?.response?.data?.message || err?.message || "Failed to load dashboard data");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }
    fetchData();
  }, [mode, year, startDate, endDate, enabled, userId]);

  const userBased = (employeeId: string | null) => {
    setUserId(employeeId);
  };

  return { dashboardData, loading, error, userBased };
};
